/**
 * Application Routes Configuration
 *
 * This file defines all routes used in the Sellpath application.
 * Routes are organized by functional sections.
 */

export const PATH = {
  /**
   * Core Navigation Routes
   * Main entry points and dashboard pages
   */
  OVERVIEW: "/home",
  DASHBOARD: "/dashboard",
  DASHBOARD_GROUP_ID: "/dashboard/[groupId]",
  HOME_PAGE: "/home",
  PROFILE: "/profile",
  KNOWLEDGE_MANAGER: "/data/knowledgemgr",
  OFFICE_GLOSSARY: "/data/office-glossary",
  OFFICE_GLOSSARY_COMPANY: "/data/office-glossary/company",
  OFFICE_GLOSSARY_LANGUAGE_REFERENCE: "/data/office-glossary/language-reference",
  OFFICE_GLOSSARY_SUGGESTIONS: "/data/office-glossary/suggestions",

  /**
   * Worksine product surfaces. These paths are a stable browser contract;
   * their target may be hosted separately while the platform is migrated.
   */
  LLMGW: "/llmgw",
  LLMGW_ADMIN: "/llmgw/admin",
  EDGE_HELPDESK: "/edge/helpdesk",
  EDGE_HELPDESK_ADMIN: "/edge/helpdesk/admin",
  SINEBUZZ: "/sinebuzz",
  SHOW_ADMIN: "/show/admin",

  /**
   * Quick Actions
   * Quick access features
   */
  QUICK_CADDY: "/quick-caddy",

  /**
   * Sales Section
   * Call management, escalations, news, activities, pipeline and quota
   */
  SALES: "/sales",
  SALES_OPEN_ACTIVITIES: "/sales/open-activities",
  SALES_ESCALATION: "/sales/escalations",
  SALES_ESCALATION_NEW: "/sales/escalations/new",
  SALES_ESCALATION_DETAIL: "/sales/escalations/[id]",
  SALES_ESCALATION_EDIT: "/sales/escalations/[id]/edit",
  SALES_ESCALATION_CUSTOMER: "/sales/escalations/customer/[id]",
  SALES_NEWS: "/sales/news",
  SALES_ACTIVITIES: "/sales/activities",
  SALES_ACTIVITIES_DETAIL: "/sales/activities/[id]",
  SALES_PIPELINE: "/sales/pipeline",
  SALES_SUPPORT_PIPELINE: "/sales/support-pipeline",
  SALES_AUTOMATION: "/sales/automation",
  SALES_AUTOMATION_DETAIL: "/sales/automation/[id]",
  SALES_CHANGE_REVIEW: "/sales/change-review",
  SALES_LEADS: "/sales/leads",
  SALES_LEADS_DETAIL: "/sales/leads/[id]",
  SALES_LEADS_INSPECTION: "/sales/leads/[id]/inspection",
  OPPORTUNITY: "/opportunity",
  OPPORTUNITY_INSPECTION: "/opportunity/[id]/inspection",
  CUSTOMER_INSPECTION: "/marketing/customer/[id]/inspection",
  SALES_QUOTA: "/sales/quota",

  /**
   * Email intelligence
   * Inbound mailbox evidence, review, analytics, and proposal outputs.
   */
  EMAIL_INTELLIGENCE: "/email-intelligence",
  EMAIL_INTELLIGENCE_INBOX: "/email-intelligence/inbox",
  EMAIL_INTELLIGENCE_ANALYTICS: "/email-intelligence/analytics",
  EMAIL_INTELLIGENCE_CONTACTS: "/email-intelligence/contacts",
  EMAIL_INTELLIGENCE_RELATIONSHIPS: "/email-intelligence/relationships",
  EMAIL_INTELLIGENCE_REVIEW: "/email-intelligence/review",
  EMAIL_INTELLIGENCE_AUTOMATION_CANDIDATES: "/email-intelligence/automation-candidates",
  EMAIL_INTELLIGENCE_REPLY_RESEARCH: "/email-intelligence/reply-research",
  EMAIL_INTELLIGENCE_TEST_LAB: "/email-intelligence/test-lab",
  EMAIL_INTELLIGENCE_SUPPORT: "/email-intelligence/support",
  EMAIL_INTELLIGENCE_VENDOR_SUPPORT: "/email-intelligence/vendor-support",

  /**
   * Equipment and commercial compatibility routes
   * Assets live under Data and engineering work lives under the Engineering
   * app. The BOM builder route remains for existing deep links and internal
   * Opportunity quote handoffs; it is not a standalone sidebar destination.
   */
  EQUIPMENT: "/equipment",
  MARKETING_ASSETS: "/marketing/assets",
  BOM_BUILDER: "/bom-builder",

  /**
   * AI Features
   * AI assistants, specialists, and work
   */
  AGENTS: "/agents",
  AGENTS_MY: "/agents/my",
  AGENTS_MY_AGENT: "/agents/my/[agentInstallationId]",
  AGENTS_WORKSPACES: "/agents/workspaces",
  AGENTS_SETTINGS: "/agents/settings",
  AGENTS_RUNTIME: "/agents/runtime",
  AGENTS_NETWORK_OS_VALIDATION: "/agents/network-os-validation",
  AGENTS_NETWORK_AUTOMATION: "/agents/network-automation",
  AGENTS_LIST: "/agents/triage",
  AGENTS_TRIAGE_INBOX: "/agents/triage/inbox",
  AGENTS_TRIAGE_INBOX_PROPOSAL: "/agents/triage/inbox/proposal/[proposalId]",
  AGENTS_TRIAGE_INBOX_PROPOSAL_EVIDENCE: "/agents/triage/inbox/proposal/[proposalId]/evidence/[evidenceId]",
  AGENTS_TRIAGE_ID: "/agents/triage/[id]",
  AGENTS_TRIAGE_ID_SUBAGENT_ID: "/agents/triage/[id]/sub-agent/[subId]",
  AGENTS_INTENT_LIBRARY: "/agents/intent-library",
  AGENTS_TOOL: "/agents/tool",
  AGENTS_TOOL_ID: "/agents/tool/[id]",
  AI_TASK: "/tasks",
  AI_TASK_DETAIL: "/tasks/[id]",
  AI_TASK_COORDINATION_DETAIL: "/tasks/coordination/[coordinationJobId]",
  AI_TASK_MEMORY_PROPOSAL: "/tasks/memory-proposals/[memoryProposalId]",
  AI_TASK_REPORTING: "/tasks/reporting/[templateId]",

  /**
   * Marketing Section
   * Customer, company, campaign and product management
   */
  MARKETING: "/marketing",
  OPS: "/ops",
  OPS_SIGN_IN: "/ops/sign-in",
  OPS_TENANTS: "/ops/tenants",
  OPS_TENANT_DETAIL: "/ops/tenants/[tenantId]",
  OPS_USERS: "/ops/users",
  OPS_USERS_DETAIL: "/ops/users/[id]",
  OPS_AUDIT: "/ops/audit",
  CUSTOMER: "/marketing/customer",
  CUSTOMER_DETAIL: "/marketing/customer/[id]",
  COMPANY: "/company",
  COMPANY_TRIAGE: "/company/signal",
  COMPANY_DELIVERABLES: "/company/deliverables",
  COMPANY_PEOPLE: "/company/people",
  COMPANY_COLLECTIVE_PERFORMANCE: "/company/collective/performance",
  COMPANY_COLLECTIVE_AGENTS: "/company/collective/agents",
  COMPANY_COLLECTIVE_FEEDBACK: "/company/collective/feedback",
  COMPANY_WORK_DETAIL: "/company/work/[id]",
  COMPANY_WORK_INSPECTION: "/company/work/[id]/inspection",
  COMPANY_SIGNAL_DETAIL: "/company/signals/[id]",
  COMPANY_DETAIL: "/company/work/[id]",
  COMPANY_INSPECTION: "/company/work/[id]/inspection",
  MARKETING_COMPANY: "/sales/accounts",
  MARKETING_COMPANY_DETAIL: "/sales/accounts/[id]",
  MARKETING_COMPANY_INSPECTION: "/sales/accounts/[id]/inspection",
  CAMPAIGN: "/marketing/campaign",
  CAMPAIGN_DETAIL: "/marketing/campaign/[id]",
  PRODUCT: "/marketing/product",
  PRODUCT_DETAIL: "/marketing/product/[id]",
  MARKETING_FILES: "/marketing/files",

  /**
   * Tools
   * Applications, templates, prompts, chains, and topics
   */
  TOOLS: "/tools",
  TOOLS_APP: "/tools/app",
  TOOLS_APP_DETAIL: "/tools/app/[id]",
  TOOLS_TEMPLATE: "/tools/template",
  TOOLS_TEMPLATE_DETAIL: "/tools/template/[id]",
  TOOLS_PROMPTS: "/tools/prompts",
  TOOLS_PROMPTS_DETAIL: "/tools/prompts/[id]",
  TOOLS_CHAINS: "/tools/chains",
  TOOLS_CHAINS_DETAIL: "/tools/chains/[id]",
  TOOLS_TOPICS: "/tools/topics",
  TOOLS_TOPICS_DETAIL: "/tools/topics/[id]",

  /**
   * System Administration
   * Users, groups, roles, and permissions management
   */
  SETTING: "/setting",
  SYSTEM: "/setting/system",
  SYSTEM_USERS: "/setting/system/users",
  SYSTEM_USERS_DETAIL: "/setting/system/users/[id]",
  SYSTEM_GROUPS: "/setting/system/groups",
  SYSTEM_GROUPS_DETAIL: "/setting/system/groups/[id]",
  SYSTEM_ROLES: "/system/roles",
  SYSTEM_ROLES_DETAIL: "/system/roles/[id]",
  SYSTEM_PERMISSIONS: "/setting/system/permissions",
  SYSTEM_PERMISSIONS_DETAIL: "/setting/system/permissions/[id]",
  SYSTEM_POLICIES: "/setting/system/policies",
  SYSTEM_INTEGRATIONS: "/setting/system-integrations",
  SYSTEM_INTEGRATIONS_INBOUND: "/setting/system-integrations/inbound",
  SYSTEM_INTEGRATIONS_MESSAGING: "/setting/system-integrations/messaging",
  SYSTEM_INTEGRATIONS_MCP: "/setting/system-integrations/mcp-servers",
  SETTING_KNOWLEDGE_PACKS: "/setting/knowledge-packs",
  SETTING_KNOWLEDGE_MODELS: "/setting/knowledge-models",
  SETTING_KNOWLEDGE_MANAGER: "/setting/knowledgemgr",
  SANDBOX_TENANTS: "/setting/tenants",
  TENANT_MANAGEMENT: "/setting/tenants/management",
  TENANT_COMPANY: "/setting/tenants/company",
  TENANT_SAMPLE_DATA: "/setting/tenants/sample-data",

  SETTING_CRM: "/setting/crm",
  SETTING_CRM_SYNC: "/setting/crm/list",
  SETTING_CRM_USER_MAPPING: "/setting/crm/user-mapping",
  SETTING_CRM_SYNC_LOGS: "/setting/crm/sync-logs",
  SETTING_CRM_SIGNALS: "/setting/crm/signals",

  /**
   * Domain-oriented Settings pages
   */
  SETTINGS_WORK_SALES_PIPELINE: "/setting/work/sales-pipeline",
  SETTINGS_WORK_SUPPORT_PIPELINE: "/setting/work/support-pipeline",
  SETTINGS_WORK_LEAD_PIPELINE: "/setting/work/lead-pipeline",
  SETTINGS_WORK_OUTCOME_PACKS: "/setting/work/outcome-packs",
  SETTINGS_WORKSPACE_OVERVIEW: "/setting/workspace/overview",
  SETTINGS_WORKSPACE_SERVICE_ACCOUNTS: "/setting/workspace/service-accounts",
  SETTINGS_AI_MODELS: "/setting/ai-models",
  SETTINGS_AI_MODELS_PROVIDERS: "/setting/ai-models/providers",
  SETTINGS_AI_MODELS_RUNTIME: "/setting/ai-models/runtime",
  SETTINGS_AI_MODELS_MODEL_MAPPING: "/setting/ai-models/model-mapping",
  SETTINGS_AI_MODELS_MODEL_OVERRIDES: "/setting/ai-models/model-overrides",
  SETTINGS_AI_MODELS_BUDGET_ALERTS: "/setting/ai-models/budget-alerts",
  SETTINGS_ACCESS_CONTROL_DASHBOARD: "/setting/access-control/dashboard",
  SETTINGS_APPS_HELPDESK_POLICY: "/setting/apps/helpdesk/policy",
  SETTINGS_APPS_HELPDESK_OPERATIONS: "/setting/apps/helpdesk/operations",
  SETTINGS_APPS: "/setting/apps",
  SETTINGS_APPS_EMAIL_VENDOR_PROFILES: "/setting/apps/email/vendor-profiles",
  SETTINGS_APPS_EMAIL_AUTOMATION_POLICY: "/setting/apps/email/automation-policy",
  SETTINGS_AGENTS_SKILLS_STORE: "/setting/agents/skills-store",

  /**
   * Coaching & Performance
   * Team performance tracking
   */
  COACHING: "/coaching",
  COACHING_TEAM_PERFORMING: "/coaching/team_performing",

  /**
   * User & Audit
   * User details and audit logs
   */
  USER_DETAIL: "/users-group/user-detail",
  AUDIT: "/setting/audit",
  AUDIT_DETAIL: "/setting/audit/[id]",

  /**
   * Billing
   */
  BILLING: "/billing",

  /**
   * Apps Section
   * Application management
   */
  APPS: "/apps",
  APPS_GROUP: "/apps/[groupId]",
  APPS_DETAIL: "/apps/[groupId]/[pageId]",
  TRAINING: "/app/training",
  TRAINING_PORTFOLIO: "/app/training/portfolio",
  TRAINING_REVIEW: "/app/training/review",

  /**
   * Workspace-first engineering application
   * Engineering is an in-product application with named workspace folders.
   */
  ENGINEERING_APP: "/app/engineering",
  ENGINEERING_NETWORK: "/app/engineering/network",
  ENGINEERING_SECURITY: "/app/engineering/security",
  ENGINEERING_CLOUD: "/app/engineering/cloud",
  ENGINEERING_CONVERSATIONS: "/app/engineering/conversations",
  ENGINEERING_REQUIREMENTS: "/app/engineering/requirements",
  ENGINEERING_DESIGNS: "/app/engineering/designs",
  ENGINEERING_VALIDATION: "/app/engineering/validation",
  ENGINEERING_BOMS: "/app/engineering/boms",
  ENGINEERING_TOOLS: "/app/engineering/tools",
  ENGINEERING_KNOWLEDGE: "/app/engineering/knowledge",
  ENGINEERING_TEMPLATES: "/app/engineering/templates",

  /**
   * Authentication & Setup
   * Sign-up, login, and initial setup
   */
  SIGN_UP: "/sign-up",
  VERIFY_EMAIL: "/auth/verify-email",
  SIGNUP_SETUP: "/signup/setup",
  LOGIN: "/sign-in",
  SIGN_IN: "/sign-in",
  RESET_PASSWORD: "/reset-password",
  NEW_PASSWORD: "/new-password",
  TWO_FA_METHOD: "/2fa/method",
  TWO_FA_VERIFY: "/2fa/verify",
  TWO_FA_RECOVERY: "/2fa/recovery"

  /**
   * Disabled Routes
   * Commented out for reference
   *
   * DASHBOARD_DEFAULT: "/dashboard/0",
   * DASHBOARD_ID: "/dashboard/[id]",
   * TODAY: "/today",
   * AI_ASSISTANT: "/ai-assistant",
   * LANDING_PAGE_EDITOR: "/pages/editor",
   */
};
/**
 * Page Source Mapping
 *
 * Maps route keys to their corresponding page source components.
 * This is used to determine which component to render for each route.
 */
export const PAGESRC = {
  // Core pages
  OVERVIEW: "dashboard",
  DASHBOARD: "dashboard",
  HOME_PAGE: "dashboard",
  PROFILE: "profile",

  // AI Tasks
  AI_TASK: "tasks_list",
  AI_TASK_DETAIL: "tasks",
  AI_TASK_COORDINATION_DETAIL: "tasks",
  AI_TASK_MEMORY_PROPOSAL: "tasks",

  // Tools
  TOOLS_APP: "tools_app_list",
  TOOLS_APP_DETAIL: "tools_app",
  TOOLS_TEMPLATE: "tools_templates_list",
  TOOLS_TEMPLATE_DETAIL: "tools_templates",

  // System administration
  SYSTEM_USERS: "system_users_list",
  SYSTEM_USERS_DETAIL: "system_users",
  SYSTEM_GROUPS: "system_groups_list",
  SYSTEM_GROUPS_DETAIL: "system_groups",
  SYSTEM_PERMISSIONS: "dashboard", // Future: "system_permissions"
  SYSTEM_ROLES: "system_roles_list",
  SYSTEM_ROLES_DETAIL: "system_roles",
  SYSTEM_POLICIES: "system_policies",
  SYSTEM_INTEGRATIONS: "system_integrations",
  SYSTEM_INTEGRATIONS_INBOUND: "system_integrations",
  SYSTEM_INTEGRATIONS_MESSAGING: "system_integrations",
  SYSTEM_INTEGRATIONS_MCP: "system_integrations",
  SETTING_KNOWLEDGE_PACKS: "setting_knowledge_packs",
  KNOWLEDGE_MANAGER: "knowledge_manager",
  SETTING_KNOWLEDGE_MANAGER: "setting_knowledge_models",
  SETTING_KNOWLEDGE_MODELS: "setting_knowledge_models",
  SETTINGS_WORKSPACE_OVERVIEW: "dashboard",
  SETTINGS_WORKSPACE_SERVICE_ACCOUNTS: "dashboard",
  SETTING: "dashboard",
  SYSTEM: "dashboard",
  OPS: "dashboard",
  OPS_TENANTS: "dashboard",
  OPS_TENANT_DETAIL: "dashboard",
  OPS_USERS: "system_users_list",
  OPS_USERS_DETAIL: "system_users",
  OPS_AUDIT: "audit_list",

  // User and audit
  USER_DETAIL: "users-group_user-detail",
  AUDIT: "audit_list",
  AUDIT_DETAIL: "audit",

  // Billing
  BILLING: "billing",

  // Apps
  APPS: "apps",
  APPS_GROUP: "apps_group",
  APPS_DETAIL: "apps_detail",
  TRAINING: "training",

  // CRM Settings
  SETTING_CRM: "dashboard", // Future: "setting_crm"
  SETTING_CRM_SYNC: "dashboard", // Future: "setting_crm_sync"
  SETTING_CRM_USER_MAPPING: "dashboard", // Future: "setting_crm_user_mapping"
  SETTING_CRM_SYNC_LOGS: "dashboard", // Future: "setting_crm_sync_logs"
  SETTING_CRM_SIGNALS: "dashboard", // Future: "setting_crm_signals"

  // Pages using dashboard as their source (temporary)
  AI_ASSISTANT: "dashboard",
  SALES: "dashboard",
  AGENTS: "agents",
  AGENTS_MY: "agents",
  AGENTS_MY_AGENT: "agents",
  AGENTS_WORKSPACES: "agents",
  AGENTS_SETTINGS: "agents",
  AGENTS_RUNTIME: "agents",
  AGENTS_LIST: "agents",
  AGENTS_TRIAGE_INBOX: "agents",
  AGENTS_TRIAGE_INBOX_PROPOSAL: "agents",
  AGENTS_TRIAGE_INBOX_PROPOSAL_EVIDENCE: "agents",
  AGENTS_TRIAGE_ID: "agents",
  AGENTS_TRIAGE_ID_SUBAGENT_ID: "agents",
  AGENTS_INTENT_LIBRARY: "agents_intent_library",
  AGENTS_TOOL: "agents_tool",
  AGENTS_TOOL_ID: "agents_tool",
  SALES_LEADS: "dashboard", // Future: "sales_leads"
  SALES_LEADS_INSPECTION: "dashboard",
  OPPORTUNITY_INSPECTION: "dashboard",
  SALES_ESCALATION: "dashboard", // Future: "sales_escalation"
  SALES_ACTIVITIES: "dashboard", // Future: "sales_activities"
  SALES_OPEN_ACTIVITIES: "salesactivity",
  SALES_NEWS: "sales_news",
  SALES_PIPELINE: "dashboard", // Future: "sales_pipeline"
  SALES_SUPPORT_PIPELINE: "dashboard",
  SALES_AUTOMATION: "sales_automation",
  SALES_CHANGE_REVIEW: "dashboard",
  SALES_QUOTA: "dashboard", // Future: "sales_quota"
  MARKETING: "dashboard", // Future: "marketing"
  CUSTOMER: "dashboard", // Future: "customer"
  CUSTOMER_INSPECTION: "dashboard",
  COMPANY: "company",
  COMPANY_PEOPLE: "company",
  COMPANY_INSPECTION: "company",
  MARKETING_COMPANY: "marketing.company",
  MARKETING_COMPANY_DETAIL: "marketing.company",
  MARKETING_COMPANY_INSPECTION: "marketing.company",
  CAMPAIGN: "dashboard", // Future: "campaign"
  PRODUCT: "dashboard", // Future: "product"
  MARKETING_FILES: "dashboard",

  // Authentication
  TWO_FA_METHOD: "two_fa_method",
  TWO_FA_VERIFY: "two_fa_verify"
};

export const ASSISTANT_PAGE_SLUG = {
  OVERVIEW: "dashboard.sales",
  HOME_PAGE: "dashboard.sales",
  DASHBOARD: "dashboard.sales",
  SALES: "dashboard.sales",
  SALES_LEADS: "sales.leads",
  SALES_LEADS_DETAIL: "sales.leads",
  SALES_LEADS_INSPECTION: "sales.leads",
  OPPORTUNITY_INSPECTION: "sales.opportunity",
  SALES_OPEN_ACTIVITIES: "dashboard.sales",
  OPPORTUNITY: "sales.opportunity",
  SALES_PIPELINE: "sales.opportunity",
  SALES_SUPPORT_PIPELINE: "dashboard.sales",
  SALES_QUOTA: "sales.quota",
  SALES_CHANGE_REVIEW: "dashboard.sales",
  PRODUCT: "marketing.product",
  PRODUCT_DETAIL: "marketing.product",
  MARKETING_FILES: "marketing.files",
  CUSTOMER: "marketing.customer",
  CUSTOMER_DETAIL: "marketing.customer",
  CUSTOMER_INSPECTION: "marketing.customer",
  COMPANY: "company",
  COMPANY_PEOPLE: "company",
  COMPANY_DETAIL: "company",
  COMPANY_INSPECTION: "company",
  MARKETING_COMPANY: "marketing.company",
  MARKETING_COMPANY_DETAIL: "marketing.company",
  MARKETING_COMPANY_INSPECTION: "marketing.company",
  CAMPAIGN: "marketing.campaign",
  CAMPAIGN_DETAIL: "marketing.campaign",
  AGENTS: "agents.setup",
  AGENTS_MY: "agents.use",
  AGENTS_MY_AGENT: "agents.use",
  AGENTS_WORKSPACES: "agents.use",
  AGENTS_SETTINGS: "agents.setup",
  AGENTS_RUNTIME: "agents.setup",
  AGENTS_LIST: "agents.setup",
  AGENTS_TRIAGE_INBOX: "agents.setup",
  AGENTS_TRIAGE_ID: "agents.refine",
  AGENTS_TRIAGE_ID_SUBAGENT_ID: "agents.refine",
  SETTING: "setting.system",
  SYSTEM: "setting.system",
  SYSTEM_USERS: "setting.system",
  SYSTEM_USERS_DETAIL: "setting.system",
  SYSTEM_GROUPS: "setting.system",
  SYSTEM_GROUPS_DETAIL: "setting.system",
  SYSTEM_ROLES: "setting.system",
  SYSTEM_ROLES_DETAIL: "setting.system",
  SYSTEM_PERMISSIONS: "setting.system",
  SYSTEM_PERMISSIONS_DETAIL: "setting.system",
  SYSTEM_POLICIES: "setting.system",
  SYSTEM_INTEGRATIONS: "setting.system",
  SYSTEM_INTEGRATIONS_INBOUND: "setting.system",
  SYSTEM_INTEGRATIONS_MESSAGING: "setting.system",
  SYSTEM_INTEGRATIONS_MCP: "setting.system",
  SETTING_KNOWLEDGE_PACKS: "setting.system",
  KNOWLEDGE_MANAGER: "knowledge",
  SETTING_KNOWLEDGE_MANAGER: "setting.system",
  SETTING_KNOWLEDGE_MODELS: "setting.system",
  SETTINGS_WORKSPACE_OVERVIEW: "setting.system",
  SETTINGS_WORKSPACE_SERVICE_ACCOUNTS: "setting.system",
  SETTING_CRM: "setting.crm",
  SETTING_CRM_SYNC: "setting.crm",
  SETTING_CRM_USER_MAPPING: "setting.crm",
  SETTING_CRM_SYNC_LOGS: "setting.crm",
  SETTING_CRM_SIGNALS: "setting.crm",
  OPS: "dashboard.manager",
  OPS_TENANTS: "dashboard.manager",
  OPS_TENANT_DETAIL: "dashboard.manager",
  OPS_USERS: "dashboard.manager",
  OPS_USERS_DETAIL: "dashboard.manager",
  OPS_AUDIT: "dashboard.manager",
  AUDIT: "setting.audit",
  AUDIT_DETAIL: "setting.audit",
  SANDBOX_TENANTS: "setting.tenants",
  TENANT_MANAGEMENT: "setting.tenants",
  TENANT_SAMPLE_DATA: "setting.tenants"
} as const;

/**
 * Helper function to get page source by path
 *
 * Maps a URL path to its corresponding page source component.
 *
 * @param path URL path to find the page source for
 * @returns The page source string or empty string if not found
 */
export const setPagesrcByPath = (path: string) => {
  const pathKey = Object.keys(PATH).find((key) => PATH[key as keyof typeof PATH] === path);
  if (pathKey) {
    return PAGESRC[pathKey as keyof typeof PAGESRC] || "";
  }
  return "";
};

export const resolveAssistantPageSlugByPath = (path: string): string => {
  const pathKey = Object.keys(PATH).find((key) => PATH[key as keyof typeof PATH] === path);
  if (!pathKey) {
    return "";
  }
  return ASSISTANT_PAGE_SLUG[pathKey as keyof typeof ASSISTANT_PAGE_SLUG] || "";
};
